import { Water } from "@mui/icons-material";
import { intl } from "LocaleWrapper";
import { Tooltip } from "@mui/material";
import { DataFormat } from "utils";
import { getStoredRegionLocale } from "utils/regionUtils";
import CargoActionActualsQuantity from "view/cargos/components/quantities/CargoActionActualsQuantity";
import { VolumeUnit, WeightUnit } from "__generated__/enums";

type CargoActionQuantityProps = {
	cargoAction: {
		weight?: number | null,
		volume?: number | null,
		measurements?: {
			locationWeight?: number | null,
			locationWeightUnit?: WeightUnit | null,
			locationVolume?: number | null,
			locationVolumeUnit?: VolumeUnit | null,
		},
	},
	cargo?: {
		isWeightPrimary: boolean,
		isVolumePrimary: boolean,
		waterLevelOptimised: boolean,
	},
	fallbackToActuals?: boolean,
	convertActualsToMton?: boolean,
	grow?: boolean
};

const CargoActionQuantity = ({ cargo, cargoAction, convertActualsToMton, fallbackToActuals, grow }: CargoActionQuantityProps) => {
	const hasActuals = cargoAction.measurements?.locationWeight || cargoAction.measurements?.locationVolume;

	const alignStyle = {
		display: "inline-block",
		minWidth: grow ? 35 : 0,
		textAlign: grow ? "left" as const : "inherit" as const,
	};

	const amountStyle = {
		display: "inline-block",
		minWidth: grow ? 55 : 0,
		textAlign: grow ? "right" as const : "inherit" as const,
	};

	const format = (value: number) => new Intl.NumberFormat(getStoredRegionLocale(), { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(value);
	const formatWeight = (value?: number | null) =>
		<><span style={amountStyle}>{format(value ?? 0)}</span> <span style={alignStyle}>{DataFormat.UNITS.MTON}</span></>;
	const formatVolume = (value?: number | null) =>
		<><span style={amountStyle}>{format(value ?? 0)}</span> <span style={alignStyle}>{DataFormat.UNITS[cargoAction.measurements?.locationVolumeUnit ?? VolumeUnit.M3]}</span></>;

	const weight = <span>{formatWeight(cargoAction.weight)}</span>;
	const volume = <span>{formatVolume(cargoAction.volume)}</span>;

	const iconStyle = { display: "inline-flex", width: 20, justifyContent: "center" as const, flexShrink: 0 };

	const waterLevel = cargo?.waterLevelOptimised ? (
		<span style={iconStyle}>
			<Tooltip title={intl.formatMessage({ id: "WATER_LEVEL_OPTIMISED" })}>
				<Water fontSize="small" color="action" />
			</Tooltip>
		</span>
	) : grow ? <span style={iconStyle} /> : null;

	const style = {
		display: "flex",
		alignItems: "center",
		flexGrow: grow ? 1 : 0,
		marginLeft: grow ? "auto" : "inherit",
		justifyContent: "flex-start",
		width: grow ? "100%" : "max-content",
		gap: 8,
	};

	const valueStyle = {
		flexShrink: 0,
		whiteSpace: "nowrap",
	};

	if (hasActuals && fallbackToActuals) {
		return (
			<CargoActionActualsQuantity
				cargo={cargo}
				measurements={cargoAction.measurements ?? {}}
				convertActualsToMton={convertActualsToMton}
				grow={grow}
			/>
		);
	}

	if (cargo?.isWeightPrimary && !cargo?.isVolumePrimary) {
		return (
			<span style={style}>
				{waterLevel}
				<span style={valueStyle}>{weight}</span>
			</span>
		);
	} else if (!cargo?.isWeightPrimary && cargo?.isVolumePrimary) {
		return (
			<span style={style}>
				{waterLevel}
				<span style={valueStyle}>{volume}</span>
			</span>
		);
	} else {
		return (
			<span style={style}>
				{waterLevel}
				<span style={valueStyle}>{weight} / {volume}</span>
			</span>
		);
	}
};

export default CargoActionQuantity;
